import { useEffect, useMemo, useState } from 'react';

import {
  LanguageContext,
  type LanguageContextValue,
  type LanguageOption,
  type TranslationParams,
} from './LanguageContext';
import { getTranslation, translations, type SupportedLanguage } from '@/locales/translations';
import { StorageService } from '@/lib/StorageService';

const storageService = new StorageService();

const defaultLanguage: SupportedLanguage = 'fr';

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<SupportedLanguage>(() => {
    if (typeof window === 'undefined') {
      return defaultLanguage;
    }
    const stored = storageService.getLanguage() as SupportedLanguage | null;
    if (stored && stored in translations) {
      return stored;
    }
    return defaultLanguage;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    storageService.setLanguage(language);
  }, [language]);

  const availableLanguages = useMemo<LanguageOption[]>(() => {
    const labels = translations[language].languages ?? translations[defaultLanguage].languages;
    return (Object.keys(translations) as SupportedLanguage[]).map((lang) => ({
      value: lang,
      label:
        typeof labels === 'object' && lang in labels
          ? String((labels as Record<string, string>)[lang])
          : lang,
    }));
  }, [language]);

  const value = useMemo<LanguageContextValue>(() => {
    const currentDictionary = translations[language];
    const fallbackDictionary = translations[defaultLanguage];

    const format = (input: string, params: TranslationParams = {}) => {
      return Object.keys(params).reduce((acc, paramKey) => {
        const value = params[paramKey];
        return acc.replace(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), String(value));
      }, input);
    };

    const translate = (key: string, params: TranslationParams = {}): string => {
      const localized = getTranslation(currentDictionary, key);
      if (typeof localized === 'string') {
        return format(localized, params);
      }
      const fallback = getTranslation(fallbackDictionary, key);
      if (typeof fallback === 'string') {
        return format(fallback, params);
      }
      return key;
    };

    return {
      language,
      setLanguage,
      t: translate,
      availableLanguages,
    };
  }, [availableLanguages, language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
